import {
  getPartStyleFromStyleInterpolation,
  type StyleFunctionProps,
  type StyleObjectMulti,
} from '@frontend/legacy-components/src/theme/styleConfigHelpers';

import { INPUT_STYLE_CONFIG } from '../input/inputStyleConfig';
import { NUMBER_INPUT_PARTS } from './numberInputParts';
import type {
  NumberInputColorScheme,
  NumberInputPart,
  NumberInputSize,
  NumberInputStyleConfig,
  NumberInputVariant,
} from './types';

const { defaultProps } = INPUT_STYLE_CONFIG;

const baseStyle: NumberInputStyleConfig['baseStyle'] = ({
  theme,
  colorMode,
}) => ({
  root: {
    width: '100%',
    '--number-input-field-padding':
      'calc(var(--number-input-stepper-width) + 0.5rem)',
  },
  field: getPartStyleFromStyleInterpolation(
    INPUT_STYLE_CONFIG.baseStyle,
    'field',
    { theme, colorMode },
  ),
  stepperGroup: {
    width: 'var(--number-input-stepper-width)',
  },
  stepper: {
    borderStart: '1px solid',
    borderStartColor: 'separator.divider',

    color: 'feature.secondary',
    zIndex: 1,

    _first: {
      borderTopEndRadius: 2,
      borderBottomWidth: 0.5,
      borderBottomColor: 'separator.divider',
    },
    _last: {
      borderBottomEndRadius: 2,
      mt: '-1px',
      borderTopWidth: 0.5,
      borderTopColor: 'separator.divider',
    },

    _hover: {
      bg: 'interaction.surfaceHover',
      zIndex: 0,
    },
    _active: {
      bg: 'interaction.surfaceActive',
    },
    _disabled: {
      backgroundColor: 'interaction.surfaceDisabled',
      color: 'interaction.disabled',
      cursor: 'not-allowed',
    },
  },
});

const getBaseSizeStyles = (
  size: NumberInputSize,
  styleFunctionProps: StyleFunctionProps<
    NumberInputVariant,
    NumberInputColorScheme
  >,
): StyleObjectMulti<NumberInputPart> => ({
  field: {
    ...getPartStyleFromStyleInterpolation(
      INPUT_STYLE_CONFIG.sizes[size],
      'field',
      styleFunctionProps,
    ),
    paddingInlineEnd: 'var(--number-input-field-padding)',
    verticalAlign: 'top',
  },
});

const sizes: NumberInputStyleConfig['sizes'] = {
  sm: ({ theme, colorMode }) => {
    const base = getBaseSizeStyles('sm', { theme, colorMode });

    return {
      ...base,
      root: {
        ...base.root,
        '--number-input-stepper-width': '0.9375rem',
      },
      stepper: {
        ...base.stepper,
        fontSize: '0.5rem',
      },
    };
  },
  md: ({ theme, colorMode }) => {
    const base = getBaseSizeStyles('md', { theme, colorMode });

    return {
      ...base,
      root: {
        ...base.root,
        '--number-input-stepper-width': '1.1875rem',
      },
      stepper: {
        ...base.stepper,
        fontSize: '0.625rem',
      },
    };
  },
  lg: ({ theme, colorMode }) => {
    const base = getBaseSizeStyles('lg', { theme, colorMode });

    return {
      ...base,
      root: {
        ...base.root,
        '--number-input-stepper-width': '1.4375rem',
      },
      stepper: {
        ...base.stepper,
        fontSize: '0.75rem',
      },
    };
  },
};

export const NUMBER_INPUT_STYLE_CONFIG: NumberInputStyleConfig = {
  parts: NUMBER_INPUT_PARTS,
  baseStyle,
  sizes,
  defaultProps,
};
